<template>
  <header class="header">
    <div
      v-click-outside="hideCallHistoryPanel"
      class="relative-parent"
      @keyup.esc="hideCallHistoryPanel"
    >
      <Btn
        variant="primary"
        class="call-history-button mrgr-"
        @click="showCallHistoryPanel"
      >
        <Media size="small" align="middle">
          <MediaRight>
            <div class="relative-parent">
              <Icon id="phone-up" />
              <div v-if="missedCalls > 0" class="call-history-button-badge">
                {{ missedCalls }}
              </div>
            </div>
          </MediaRight>
          <MediaBody>
            {{ $t('calls-history.label') }}
          </MediaBody>
        </Media>
      </Btn>
      <GrowTransition>
        <CallHistoryPanel v-if="callHistoryPanelVisible" />
      </GrowTransition>
    </div>
    <Box size="small" class="text-negative">
      <SearchBar />
    </Box>
    <Box size="small" class="text-negative">
      <MapFilters />
    </Box>
    <Box size="small" class="text-negative">
      <ListInline class="text-right justify-content-end">
        <li><VoipStatus /></li>
        <li v-click-outside="hideUserPopover" class="relative-parent">
          <button
            type="button"
            class="btn btn--bare"
            data-test="user-account-popover-trigger"
            @click="userPopoverVisible = !userPopoverVisible"
          >
            {{ userLabel }}
            <Icon
              :id="userPopoverVisible ? 'chevron-up' : 'chevron-down'"
              :scale="0.5"
              class="mrgl--"
            />
          </button>
          <Popover
            v-if="userPopoverVisible"
            position="bottom end"
            class="call-deviation-popover popover--no-arrow"
          >
            <ul class="list-stacked list-stacked--divided">
              <li>
                <button class="btn btn--bare" @click="toggleRingtoneDialog">
                  {{ $t('ringtone.label') }}
                </button>
              </li>
              <li>
                <button class="btn btn--bare" @click="toggleDeviationDialog">
                  {{ $t('call-deviation.enable') }}
                </button>
              </li>
              <li v-if="user.can_access_routing_profiles">
                <button class="btn btn--bare" @click="toggleExploitationCenter">
                  {{ $t('exploitationcenter.label') }}
                </button>
              </li>
              <li>
                <button class="btn btn--bare" @click.prevent="logout">
                  {{ $t('authentication.logout') }}
                </button>
              </li>
            </ul>
          </Popover>
        </li>
      </ListInline>
    </Box>

    <Deviation v-if="currentUser" />
    <Ringtone />
    <ExploitationCenter />
  </header>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';

import eventBus from '@/eventBus';

import VoipStatus from '@/components/voip/Status';
import Deviation from '@/components/deviation/Deviation';
import ExploitationCenter from '@/components/exploitationcenter/ExploitationCenter';
import Ringtone from '@/components/ringtone/Ringtone';
import CallHistoryPanel from '@/components/call-history/CallHistoryPanel';
import GrowTransition from '@/components/transitions/Grow';
import SearchBar from '@/components/search/SearchBar';
import Popover from '@/components/ui/Popover';
import MapFilters from '@/components/map-filters/MapFilters';

export default {
  components: {
    VoipStatus,
    Deviation,
    ExploitationCenter,
    Ringtone,
    CallHistoryPanel,
    GrowTransition,
    SearchBar,
    Popover,
    MapFilters,
  },

  data() {
    return {
      userPopoverVisible: false,
    };
  },

  computed: {
    ...mapState('auth', ['user']),
    ...mapState('voip', ['missedCalls']),
    ...mapGetters('auth', ['currentUser']),
    ...mapState('ui', ['callHistoryPanelVisible']),

    userLabel() {
      return this.currentUser
        ? `${this.currentUser.first_name} ${this.currentUser.last_name}`
        : this.user.username;
    },
  },

  methods: {
    ...mapMutations('ui', ['setCallHistoryPanelVisibility']),
    ...mapMutations('voip', ['setMissedCalls']),
    ...mapActions('auth', ['logout']),

    showCallHistoryPanel() {
      this.setCallHistoryPanelVisibility(!this.callHistoryPanelVisible);
      this.setMissedCalls(0);
    },

    hideCallHistoryPanel() {
      if (this.callHistoryPanelVisible) {
        this.setCallHistoryPanelVisibility(false);
      }
    },

    hideUserPopover() {
      this.userPopoverVisible = false;
    },

    toggleDeviationDialog() {
      eventBus.$emit('toggleDeviationDialog');
      this.userPopoverVisible = false;
    },

    toggleRingtoneDialog() {
      eventBus.$emit('toggleRingtoneDialog');
      this.userPopoverVisible = false;
    },

    toggleExploitationCenter() {
      eventBus.$emit('toggleExploitationCenter');
      this.userPopoverVisible = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.header {
  display: grid;
  grid-template-columns: 155px minmax(auto, 700px) auto auto;
  align-items: center;

  position: relative;
  z-index: 2; // Allow to put CallHistoryPanel behind

  background-color: $alt-700;
}

.call-history-button {
  max-width: 145px;
  padding: $spacing-unit-small * $ratio $spacing-unit-default;

  font-size: rem(14px);
  line-height: 1.2;
  text-align: left;
  white-space: normal;

  border-radius: 0 40px 40px 0 / 0 130px 130px 0;
  box-shadow: none;

  &:active {
    transform: none;
  }
}

.call-history-button-badge {
  padding: 3px 7px;

  position: absolute;
  right: -10px;
  bottom: -6px;

  text-align: center;

  background-color: $danger-500;

  border-radius: 20px;
}

.call-deviation-popover {
  margin-top: 20px;
  padding: 15px;
  min-width: 250px;

  right: -10px;

  text-align: left;
  color: white;

  background-color: $alt-600;
  border: 0;
  border-radius: 0;

  button,
  a {
    &:hover,
    &:focus {
      font-weight: bold;
    }
  }
}
</style>
